import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { ERoutes } from "@utils/constants";

import AuthGuard from "@/guards/AuthGuard";
import SecondRootLayout from "@/layouts/SecondRootLayout";

const Album = lazy(() => import("@/pages/Album"));
const CuratedPlaylist = lazy(() => import("@/pages/CuratedPlaylist"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const FreeBackground = lazy(() => import("@pages/CatalogLanding/components/FreeBackground"));
const Conditions = lazy(() => import("@/pages/Conditions"));
const ContactAndLicensing = lazy(() => import("@/pages/ContactAndLicensing"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Imprint = lazy(() => import("@/pages/Imprint"));
const Newsletter = lazy(() => import("@/pages/Newsletter"));
const NewsletterPreferences = lazy(() => import("@/pages/NewsletterPreferences"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const LicenceTermsPage = lazy(() => import("@/pages/LicenceTermsPage"));
const WhatsNew = lazy(() => import("@/pages/WhatsNew"));
const Withdraw = lazy(() => import("@/pages/Withdraw"));

const Favorites = lazy(() => import("@/pages/Favorites"));
const Submit = lazy(() => import("@/pages/Submit"));
const Track = lazy(() => import("@pages/Track"));
const Deals = lazy(() => import("@/pages/Deals"));
const DownloadInstruction = lazy(() => import("@/pages/DownloadInstruction"));
const PartnerProgram = lazy(() => import("@pages/PartnerProgram"));
const Donation = lazy(() => import("@pages/Donation"));

const AccountDashboard = lazy(() => import("@/pages/AccountDashboard"));
const AccountOrdersPage = lazy(() => import("@/pages/AccountOrdersPage"));
const AccountDownloadsPage = lazy(() => import("@/pages/AccountDownloadsPage"));
const AccountLicensesPage = lazy(() => import("@/pages/AccountLicensesPage"));
const AccountSubscription = lazy(() => import("@/pages/AccountSubscription"));
const AccountNotifications = lazy(() => import("@/pages/AccountNotifications"));
const AccountDataPrivacy = lazy(() => import("@/pages/AccountDataPrivacy"));
const AccountLegal = lazy(() => import("@/pages/AccountLegal"));
const DesignSystem = lazy(() => import("@/pages/DesignSystem"));
const LicensingRedesign = lazy(() => import("@/pages/LicensingRedesign/index"));
const FreeYoutubeMusic = lazy(() => import("@/pages/FreeYouTubeMusic/index"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const AccountDeleted = lazy(() => import("@/pages/AccountDeleted"));

const privateRoutes = [
  {
    path: ERoutes.Favorites,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <Favorites />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.Profile,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountDashboard />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountOrders,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountOrdersPage />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountDownloads,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountDownloadsPage />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountVideos,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountLicensesPage />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountSubscription,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountSubscription />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountNotifications,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountNotifications />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: ERoutes.AccountDataPrivacy,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountDataPrivacy />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },

  {
    path: "/account/legal" as ERoutes,
    element: (
      <SecondRootLayout>
          <AuthGuard>
            <AccountLegal />
          </AuthGuard>
        </SecondRootLayout>
    ),
  },
];

const publicRoutes = [
  {
    path: ERoutes.Catalog,

    element: (
      <SecondRootLayout>
        <Catalog />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.SelectedQuickFilter,

    element: (
      <SecondRootLayout>
        <Catalog />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Album,

    element: (
      <SecondRootLayout>
        <Album />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Contact,
    element: (
      <SecondRootLayout>
        <ContactAndLicensing />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Licensing,
    element: (
      <SecondRootLayout>
        <ContactAndLicensing />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.FreeUseTerms,
    element: <Navigate to={ERoutes.FreeYoutubeMusic} replace />,
  },
  {
    // An old typo — singular — that Search Console still lists among the
    // indexed addresses (D6). Zero clicks in twelve months, which is why this
    // is a route rather than a rule in the function that serves every request:
    // the cost of carrying it should match what it earns.
    path: "/download-instruction",
    element: <Navigate to={ERoutes.DownloadInstruction} replace />,
  },
  {
    path: ERoutes.Pricing,
    element: (
      <SecondRootLayout>
        <Pricing />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Conditions,
    element: (
      <SecondRootLayout>
        <Conditions />
      </SecondRootLayout>
    ),
  },
  // The Terms lived at /conditions until 2026-07-18. Keep the old path
  // redirecting so existing links (emails, checkout confirmations already
  // sent, anything indexed) don't 404.
  {
    path: "/conditions",
    element: <Navigate to={ERoutes.Conditions} replace />,
  },
  {
    path: ERoutes.Submit,
    element: (
      <SecondRootLayout>
        <Submit />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Newsletter,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <Newsletter />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.NewsletterSuccess,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <Newsletter />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.AccountDeleted,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <AccountDeleted />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.NewsletterUnsubscribe,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <Newsletter />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.NewsletterPreferences,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <NewsletterPreferences />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Unsubscribe,
    element: (
      <SecondRootLayout isNewsletter={false}>
        <Unsubscribe />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Privacy,
    element: (
      <SecondRootLayout>
        <Privacy />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.RefundPolicy,
    element: (
      <SecondRootLayout>
        <RefundPolicy />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.LicenceTerms,
    element: (
      <SecondRootLayout>
        <LicenceTermsPage />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.WhatsNew,
    element: (
      <SecondRootLayout>
        <WhatsNew />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.CookiePolicy,
    element: (
      <SecondRootLayout>
        <CookiePolicy />
      </SecondRootLayout>
    ),
  },
  {
    // Deliberately NOT behind AuthGuard — someone locked out of their account
    // must still be able to withdraw (§ 13a FAGG).
    path: ERoutes.Withdraw,
    element: (
      <SecondRootLayout>
        <Withdraw />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Imprint,
    element: (
      <SecondRootLayout>
        <Imprint />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Home,
    element: (
      <SecondRootLayout>
        <HomePage />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.CatalogLandingFreeBackground,
    element: (
      <SecondRootLayout>
        <FreeBackground />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.CatalogLandingLicensing,
    element: (
      <SecondRootLayout>
        <LicensingRedesign />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.FreeYoutubeMusic,
    element: (
      <SecondRootLayout>
        <FreeYoutubeMusic />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.HowItWorks,
    element: (
      <SecondRootLayout>
        <HowItWorks />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.DownloadInstruction,
    element: (
      <SecondRootLayout>
        <DownloadInstruction />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Deals,
    element: (
      <SecondRootLayout>
        <Deals />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.SelectedDeal,
    element: (
      <SecondRootLayout>
        <Deals />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Track,
    element: (
      <SecondRootLayout>
        <Track />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.MUSIC_CREATOR_PROGRAM,
    element: (
      <SecondRootLayout>
        <PartnerProgram />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.Donation,
    element: (
      <SecondRootLayout>
        <Donation />
      </SecondRootLayout>
    ),
  },

  {
    path: `${ERoutes.OrderSuccess}/:orderId`,
    element: (
      <SecondRootLayout>
        <OrderSuccess />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.OrderSuccess,
    element: (
      <SecondRootLayout>
        <OrderSuccess />
      </SecondRootLayout>
    ),
  },
  {
    path: ERoutes.DesignSystem,
    element: <DesignSystem />,
  },
  {
    path: ERoutes.CuratedPlaylist,
    element: (
      <SecondRootLayout>
        <CuratedPlaylist />
      </SecondRootLayout>
    ),
  },
];
const routes = [
  {
    path: "*",
    element: (
      <SecondRootLayout>
        <NotFound />
      </SecondRootLayout>
    ),
  },

  ...privateRoutes,
  ...publicRoutes,
];
export default routes;
